import { Box, CircularProgress, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import type { FC, UIEvent } from "react"
import { useFormContext } from "react-hook-form"

import { FilterCheckbox } from "@/components/common/Filters"
import { InputField } from "@/components/common/InputField"
import type { ProductListFilter } from "@/graphql/codegen/graphql"
import { useGetSuppliers } from "@/hooks/useGetSuppliers"
import useDebounce from "@/utils/useDebounce"

interface SupplierFilterProps {
  onFilterChange: (name: keyof ProductListFilter, value: any) => void
}

export const SupplierFilter: FC<SupplierFilterProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const suppliersValue = watch("companyIds") || []
  const debouncedSearchString = useDebounce(searchTerm, 300)

  const { suppliers, isFetching, loadMore } = useGetSuppliers({ query: debouncedSearchString })

  const handleFilterChange = (newValue: string[]) => {
    onFilterChange("companyIds", newValue)
  }

  const canLoadMore = (element: UIEvent) => {
    const el = element.currentTarget
    return !isFetching && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Supplier Name
      </Typography>

      <Box className='flex-none p-4'>
        <InputField
          placeholder='Search by Name'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchOutlined className='text-gray-600' />}
          endAdornment={isFetching && <CircularProgress size={20} />}
        />
      </Box>

      <Box className='flex grow flex-col overflow-hidden'>
        <Box
          className='grow overflow-y-auto overflow-x-hidden px-8 py-2'
          onScroll={(event) => {
            if (canLoadMore(event)) {
              loadMore()
            }
          }}
        >
          {suppliers.map((supplier) => (
            <FilterCheckbox
              key={supplier.companyId}
              label={`${supplier.name}`}
              value={supplier.companyId}
              control={control}
              name='companyIds'
              values={suppliersValue}
              onFilterChange={handleFilterChange}
            />
          ))}

          {isFetching && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
